import maplibregl, { type Map as MapLibreMap, type GeoJSONSource } from "maplibre-gl";
import {
  DASHED_LINE_FILTER,
  DOTTED_LINE_FILTER,
  DRAFT_LINE_COLOR,
  DRAFT_LINE_OPACITY,
  DRAFT_LINE_WIDTH,
  ROUTE_LINE_LAYOUT,
  ROUTE_LINE_SHADOW_PAINT,
  SOLID_LINE_FILTER,
} from "../../shared/line-paint.ts";
import {
  DEFAULT_QUEST_ROUTE_COLOR,
  routeFillFeatureCollection,
  routePaintFeatureCollection,
  type RouteFillFeature,
  type RoutePaintFeature,
} from "../../shared/quest-route-paint.ts";
import { REGION_FILL_ANTIALIAS } from "../../shared/region-fill.ts";
import { DASHED_PATTERN, DOTTED_PATTERN, routeLineScaleFactor, scaledRouteLinePaint } from "../../shared/route-line-scale.ts";
import type { LngLat } from "../../shared/types.ts";
import { mapHasLayer, mapHasStyleObject, mapStyleReady } from "../../shared/map-style-safe.ts";

/** Match the route editor fill layer (`map-editor.ts`). */
const ROUTE_FILL_OPACITY = 0.42;

export interface StyledRouteLayerIds {
  fillSource: string;
  fill: string;
  source: string;
  shadowSolid: string;
  shadowDashed: string;
  shadowDotted: string;
  solid: string;
  dashed: string;
  dotted: string;
}

export function styledRouteLayerIds(prefix: string): StyledRouteLayerIds {
  return {
    fillSource: `${prefix}-fill-src`,
    fill: `${prefix}-fill`,
    source: `${prefix}-src`,
    shadowSolid: `${prefix}-shadow-solid`,
    shadowDashed: `${prefix}-shadow-dash`,
    shadowDotted: `${prefix}-shadow-dot`,
    solid: `${prefix}-solid`,
    dashed: `${prefix}-dash`,
    dotted: `${prefix}-dot`,
  };
}

/** MapLibre layers that read stroke/width/pattern from feature properties (same as the route editor). */
export function ensureStyledRouteLayers(map: MapLibreMap, ids: StyledRouteLayerIds): void {
  if (!mapHasStyleObject(map)) return;
  try {
    if (!map.getSource(ids.fillSource)) {
      map.addSource(ids.fillSource, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: ids.fill,
        type: "fill",
        source: ids.fillSource,
        paint: {
          "fill-color": ["coalesce", ["get", "color"], "#3b82f6"],
          "fill-opacity": ROUTE_FILL_OPACITY,
          "fill-antialias": REGION_FILL_ANTIALIAS,
        },
      });
    }
    if (!map.getSource(ids.source)) {
      map.addSource(ids.source, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      const lineColor = DRAFT_LINE_COLOR as maplibregl.ExpressionSpecification;
      const lineWidth = DRAFT_LINE_WIDTH as maplibregl.ExpressionSpecification;
      const shadowPaint = ROUTE_LINE_SHADOW_PAINT as maplibregl.LineLayerSpecification["paint"];
      const shadow = (id: string, filter: maplibregl.FilterSpecification) => {
        map.addLayer({
          id,
          type: "line",
          source: ids.source,
          filter,
          layout: ROUTE_LINE_LAYOUT,
          paint: shadowPaint,
        });
      };
      shadow(ids.shadowSolid, SOLID_LINE_FILTER as maplibregl.FilterSpecification);
      shadow(ids.shadowDashed, DASHED_LINE_FILTER as maplibregl.FilterSpecification);
      shadow(ids.shadowDotted, DOTTED_LINE_FILTER as maplibregl.FilterSpecification);
      map.addLayer({
        id: ids.solid,
        type: "line",
        source: ids.source,
        filter: SOLID_LINE_FILTER as maplibregl.FilterSpecification,
        layout: ROUTE_LINE_LAYOUT,
        paint: { "line-color": lineColor, "line-width": lineWidth, "line-opacity": DRAFT_LINE_OPACITY },
      });
      map.addLayer({
        id: ids.dashed,
        type: "line",
        source: ids.source,
        filter: DASHED_LINE_FILTER as maplibregl.FilterSpecification,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": lineWidth,
          "line-opacity": DRAFT_LINE_OPACITY,
          "line-dasharray": [...DASHED_PATTERN],
        },
      });
      map.addLayer({
        id: ids.dotted,
        type: "line",
        source: ids.source,
        filter: DOTTED_LINE_FILTER as maplibregl.FilterSpecification,
        layout: ROUTE_LINE_LAYOUT,
        paint: {
          "line-color": lineColor,
          "line-width": lineWidth,
          "line-opacity": DRAFT_LINE_OPACITY,
          "line-dasharray": [...DOTTED_PATTERN],
        },
      });
    }
  } catch {
    /* style torn down while adding quest route layers */
  }
}

export function setStyledRouteData(
  map: MapLibreMap,
  ids: StyledRouteLayerIds,
  paint: readonly RoutePaintFeature[] | null,
  coordinates: LngLat[] | null,
  fallbackColor = DEFAULT_QUEST_ROUTE_COLOR,
  fill: readonly RouteFillFeature[] | null = null,
): void {
  if (!mapHasStyleObject(map)) return;
  try {
    ensureStyledRouteLayers(map, ids);
    const fillSrc = map.getSource(ids.fillSource) as GeoJSONSource | undefined;
    fillSrc?.setData(routeFillFeatureCollection(fill));
    const src = map.getSource(ids.source) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(routePaintFeatureCollection(paint, coordinates, fallbackColor));
  } catch {
    /* never throw from route paint during style swap */
  }
}

/**
 * Downscale route strokes on small embeds/thumbnails; full-size maps pass factor 1. Only touches
 * layers that exist: a resize can land before the style loads, when adding layers throws.
 */
export function applyStyledRouteLineScale(map: MapLibreMap, ids: StyledRouteLayerIds, factor: number): void {
  if (!mapStyleReady(map)) return;
  try {
    const scaled = scaledRouteLinePaint(factor);
    const shadowIds = [ids.shadowSolid, ids.shadowDashed, ids.shadowDotted];
    for (const id of shadowIds) {
      if (!mapHasLayer(map, id)) continue;
      map.setPaintProperty(id, "line-width", scaled.shadowWidth);
      map.setPaintProperty(id, "line-blur", scaled.shadowBlur);
      map.setPaintProperty(id, "line-translate", scaled.shadowTranslate);
    }
    if (mapHasLayer(map, ids.solid)) map.setPaintProperty(ids.solid, "line-width", scaled.lineWidth);
    if (mapHasLayer(map, ids.dashed)) {
      map.setPaintProperty(ids.dashed, "line-width", scaled.lineWidth);
      map.setPaintProperty(ids.dashed, "line-dasharray", scaled.dashedDasharray);
    }
    if (mapHasLayer(map, ids.dotted)) {
      map.setPaintProperty(ids.dotted, "line-width", scaled.lineWidth);
      map.setPaintProperty(ids.dotted, "line-dasharray", scaled.dottedDasharray);
    }
  } catch {
    /* style mid-resize */
  }
}

export function applyStyledRouteLineScaleForContainer(
  map: MapLibreMap,
  ids: StyledRouteLayerIds,
  widthPx: number,
  heightPx: number,
): void {
  applyStyledRouteLineScale(map, ids, routeLineScaleFactor(widthPx, heightPx));
}
